import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2,
  ArrowLeft,
  Package,
  Search,
  MapPin,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { fetchEdificios } from '@/pages/parametrizacion/edificios/Services/edificios.services';
import { fetchTotalesPorEdificios } from '@/services/inventarioLenceriaVertical.service';

const InventarioVerticalPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [edificios, setEdificios] = useState([]);
  const [filteredEdificios, setFilteredEdificios] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [inventariosPorEdificio, setInventariosPorEdificio] = useState({});
  const [totalGeneralPrendas, setTotalGeneralPrendas] = useState(0);

  useEffect(() => {
    loadEdificios();
  }, []);

  useEffect(() => {
    filterEdificios();
  }, [searchTerm, edificios]);

  const loadEdificios = async () => {
    setIsLoading(true);
    try {
      const response = await fetchEdificios({
        limit: 1000,
        offset: 1,
        query: { 
          estado: 'Activo',
          tipoLogistica: 'vertical' // Solo edificios verticales
        }
      });
      const edificiosData = response.data || [];
      setEdificios(edificiosData);
      
      // Cargar inventario para cada edificio
      await loadInventariosPorEdificio(edificiosData);
    } catch (error) {
      console.error('Error al cargar edificios:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los edificios',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const loadInventariosPorEdificio = async (edificiosData) => {
    try {
      // Extraer IDs de edificios
      const edificioIds = edificiosData.map(edificio => edificio._id);
      
      // Llamar al endpoint optimizado que solo trae totales
      const result = await fetchTotalesPorEdificios(edificioIds);
      
      setInventariosPorEdificio(result.totalesPorEdificio);
      setTotalGeneralPrendas(result.totalGeneral);
    } catch (error) {
      console.error('Error al cargar inventarios:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los totales de inventario',
        variant: 'destructive',
      });
    }
  };

  const filterEdificios = () => {
    if (!searchTerm.trim()) {
      setFilteredEdificios(edificios);
      return;
    }

    const filtered = edificios.filter(edificio =>
      edificio.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      edificio.ciudad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      edificio.direccion?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredEdificios(filtered);
  };

  const handleEdificioClick = (edificio) => {
    navigate(`/lavanderia/inventario-vertical/${edificio._id}`, {
      state: { edificio }
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/lavanderia')}
                  className="h-10 w-10"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="p-3 bg-purple-100 rounded-full">
                  <Building2 className="h-8 w-8 text-purple-600" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-2xl font-bold text-foreground">
                    Inventario por Edificio Vertical
                  </CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Gestiona el inventario de lencería de cada edificio vertical
                  </CardDescription>
                </div>
                {/* Total General de Prendas */}
                {totalGeneralPrendas > 0 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Package className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Total General</p>
                        <p className="text-2xl font-bold text-purple-700">{totalGeneralPrendas.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">prendas en inventario</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar por nombre, ciudad o dirección..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Lista de Edificios */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                Edificios Verticales ({filteredEdificios.length})
              </CardTitle>
              {isLoading && (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-4">Cargando edificios...</p>
              </div>
            ) : filteredEdificios.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium mb-2">
                  {searchTerm ? 'No se encontraron edificios' : 'No hay edificios verticales'}
                </p>
                <p className="text-sm text-gray-400">
                  {searchTerm ? 'Intenta con otros términos de búsqueda' : 'Parametriza edificios como verticales'}
                </p>
              </div>
            ) : (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
              >
                {filteredEdificios.map((edificio) => (
                  <motion.div key={edificio._id} variants={itemVariants}>
                    <Card
                      className="h-full cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] border-2 hover:border-purple-300"
                      onClick={() => handleEdificioClick(edificio)}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between mb-2">
                          <div className="bg-purple-100 p-2 rounded-lg">
                            <Building2 className="h-6 w-6 text-purple-600" />
                          </div>
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                            Vertical
                          </Badge>
                        </div>
                        <CardTitle className="text-lg font-semibold text-gray-900 leading-tight">
                          {edificio.nombre}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span>{edificio.ciudad}</span>
                        </div>
                        {edificio.direccion && (
                          <p className="text-xs text-gray-500 line-clamp-2">
                            {edificio.direccion}
                          </p>
                        )}
                        
                        {/* Total de prendas del edificio */}
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Package className="h-4 w-4 text-purple-600" />
                              <span className="text-xs text-gray-600">Pendientes</span>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-purple-700">
                                {inventariosPorEdificio[edificio._id] !== undefined
                                  ? inventariosPorEdificio[edificio._id].toLocaleString()
                                  : '...'}
                              </p>
                              <p className="text-xs text-gray-500">prendas</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="pt-2">
                          <Button
                            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                            size="sm"
                          >
                            <Package className="h-4 w-4 mr-2" />
                            Ver Inventario
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default InventarioVerticalPage;
